import type { MediaItem, Notice, NoticeLayer } from '@cornerstone/shared';

/**
 * 슬라이드 저장 토스트 — 기기·미리보기에 언제 반영되는지 알린다(SCDS-116).
 *
 * 송출 중인 슬라이드는 저장하면 바로 기기로 나간다(로컬 실측 2초대, 미리보기 창은 5~10초 주기로 다시 읽는다).
 * 늦어지는 경우만 이유를 붙인다 — 새로 넣은 파일은 기기가 다 받은 뒤 바뀌고, 꺼진 기기는 다시 연결될 때 받는다.
 * 조직·중앙 관리자가 다른 그룹 플레이리스트에 담으면 그 그룹 사본이 생기고(crossGroupCopy), 사본은 원본을
 * 따라 바뀌지 않는다. QA가 원본을 고치고 '반영 안 됨'으로 본 원인이 이것이라 송출 중인 사본이 있으면 그렇게 알린다.
 */
/** 그룹 사본 표식 'group-copy:<그룹>:<원본 id>[#원본 지문.사본 지문]'의 원본 id(지문은 2026-10-14부터 붙는다). */
export function copySourceId(seedSourceId: string | null | undefined): string | null {
  const match = /^group-copy:[^:]+:([^#]+)(?:#[0-9a-f.]+)?$/.exec(seedSourceId ?? '');
  return match ? match[1]! : null;
}

export function slideSaveMessage(saved: Notice, notices: readonly Notice[], before: readonly NoticeLayer[] | null,
  mediaById: ReadonlyMap<string, Pick<MediaItem, 'sizeBytes'>>): { text: string; kind: 'success' | 'info' } {
  const copy = notices.find(n => n.scheduled && copySourceId(n.seedSourceId) === saved.id);
  const where = copy?.scheduledIn?.find(w => !w.startsWith('다른 그룹'));
  const copyNote = copy ? `송출 중인 ${where ? `‘${where}’의 ` : ''}그룹 사본은 원본을 따라 바뀌지 않아요. 라이브러리에서 편성 중 표시가 있는 같은 이름 슬라이드를 고쳐 주세요.` : '';
  if (!saved.scheduled) return copy ? { kind: 'info', text: `저장했지만 기기 화면은 그대로입니다. ${copyNote}` } : { kind: 'success', text: '저장되었습니다.' };
  const had = new Set((before ?? []).flatMap(layer => 'mediaId' in layer ? [layer.mediaId] : []));
  const added = [...new Set(saved.layers.flatMap(layer => 'mediaId' in layer && !had.has(layer.mediaId) ? [layer.mediaId] : []))];
  const mb = Math.ceil(added.reduce((sum, id) => sum + (mediaById.get(id)?.sizeBytes ?? 0), 0) / 1_048_576);
  return { kind: 'success', text: ['저장되었습니다. 이 슬라이드를 재생하는 기기에는 5초, 미리보기 창에는 10초 안에 반영됩니다.',
    added.length ? `새로 넣은 파일${mb ? `(${mb}MB)` : ''}은 기기가 내려받은 뒤 바뀌어 더 걸릴 수 있어요.` : '',
    '꺼져 있던 기기는 다시 연결될 때 바뀝니다.', copyNote].filter(Boolean).join(' ') };
}
